import {useQuery} from '@tanstack/react-query';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {AsyncStatusesEnum} from '../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {
  buildChiliPiperUrl,
  ChiliPiperProductionRoutersEnum,
} from '../../../../libs/shared/core/src/services/chilipiper/chilipiper';
import {Dialog} from '../../../../libs/shared/ui/src/components/dialogs/dialog';
import {DialogHeader} from '../../../../libs/shared/ui/src/components/dialogs/dialogHeader';
import {AsyncLoader} from '../../../../libs/shared/ui/src/components/loaders/asyncLoader';
import {center} from '../../../../libs/shared/ui/src/styles/mixins';
import {useAppDispatch} from '../../../helpers/appReduxHelpers';
import {fetchChiliPiperRoutingParametersAction} from '../../../store/data/onboarding/onboardingActions';

/*
 * Props.
 */

interface ChiliPiperDialogProps {
  title: ReactNode;
  router: ChiliPiperProductionRoutersEnum;
  onRequestClose: () => void;
}

/*
 * Style.
 */

// This comfortably fits the Chili Piper scheduling page without scrolling.
const contentWidth = 800;
const contentHeight = 700;

const StyledContentDiv = styled.div`
  height: ${contentHeight}px;

  ${center()}
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: 0 none;
`;

/*
 * Component.
 */

export const ChiliPiperDialog: FC<ChiliPiperDialogProps> = (props) => {
  const dispatch = useAppDispatch();
  const fetchChiliPiperRoutingParametersAsync = async () =>
    dispatch(fetchChiliPiperRoutingParametersAction());
  const {
    data: routingParameters,
    isLoading,
    refetch,
  } = useQuery(['chili-piper-dialog', props.router], fetchChiliPiperRoutingParametersAsync);

  return (
    <Dialog width={contentWidth} onRequestClose={props.onRequestClose}>
      <DialogHeader title={props.title} onRequestClose={props.onRequestClose} />
      <StyledContentDiv>
        {routingParameters ? (
          <StyledIframe
            src={buildChiliPiperUrl(props.router, routingParameters)}
            referrerPolicy="strict-origin-when-cross-origin"
          />
        ) : (
          <AsyncLoader
            status={isLoading ? AsyncStatusesEnum.LOADING : AsyncStatusesEnum.FAILED}
            onRetryClick={() => refetch()}
          />
        )}
      </StyledContentDiv>
    </Dialog>
  );
};
